/**
 * Barber Boss product detail page behavior.
 *
 *  - <bb-pdp-gallery>  thumbnail rail (desktop) / scroll-snap track (mobile), counter, dots, zoom
 *  - <bb-pdp-buy>      variant + pack + quantity picker, price / points / shipping meter, add to cart
 *  - <bb-pdp-sticky>   mobile add-to-cart bar, visible while the main button is out of view
 *  - <bb-pdp-fbt>      "Suelen comprarse juntos" bundle (lives in its own section)
 *
 * Add to cart follows the bb-quick-view pattern: POST /cart/add.js and dispatch
 * @shopify/events' CartLinesUpdateEvent from the triggering element so Horizon's cart
 * drawer updates and bb-fly-to-cart can fly the product image. When the events module is
 * unavailable the buy box falls back to a normal form submit.
 */

const MOBILE_QUERY = '(max-width: 749px)';
const FALLBACK_LINES_UPDATE = 'shopify:cart:lines-update';
const REDUCE_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @type {Promise<any | null> | null} */
let eventsPromise = null;

/** Loads @shopify/events once; resolves to null when the import map does not provide it. */
function getEvents() {
  if (!eventsPromise) {
    eventsPromise = import('@shopify/events').catch((error) => {
      console.warn('[bb-pdp] @shopify/events unavailable', error);
      return null;
    });
  }
  return eventsPromise;
}

function routes() {
  return window.Theme?.routes || {};
}

/** @returns {Promise<any>} the /cart.js payload */
async function fetchCart() {
  const url = routes().cart_url ? `${routes().cart_url}.js` : '/cart.js';
  const response = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
  return response.json();
}

/** @param {string} currency ISO code */
function moneyFormatter(currency) {
  try {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: currency || 'EUR' });
  } catch {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' });
  }
}

/**
 * Replaces the content of `element` with `template` where `[token]` becomes a <strong>.
 * Built with DOM nodes so merchant-provided text is never parsed as HTML.
 * @param {Element} element
 * @param {string} template
 * @param {string} token
 * @param {string} value
 */
function setTemplate(element, template, token, value) {
  const marker = `[${token}]`;
  const index = template.indexOf(marker);
  if (index === -1) {
    element.textContent = `${template} ${value}`.trim();
    return;
  }
  const strong = document.createElement('strong');
  strong.textContent = value;
  element.replaceChildren(
    document.createTextNode(template.slice(0, index)),
    strong,
    document.createTextNode(template.slice(index + marker.length))
  );
}

/**
 * Adds lines to the cart and tells Horizon about it. Dispatches CartLinesUpdateEvent (when
 * available) before the request so the drawer shows its loading state, and resolves it with
 * the refreshed cart. Falls back to the cart page when the events module is missing.
 * @param {Array<{ id: number, quantity: number }>} items
 * @param {Element} source element that triggered the add; bb-fly-to-cart looks for an image around it
 * @param {string} sourceId
 * @returns {Promise<any>} the refreshed /cart.js payload
 */
async function addLines(items, source, sourceId) {
  const events = await getEvents();
  const CartLinesUpdateEvent = events?.CartLinesUpdateEvent ?? null;

  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
    .filter(Boolean);

  const addUrl = routes().cart_add_url || '/cart/add.js';
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  let deferred = null;
  if (CartLinesUpdateEvent) {
    deferred = CartLinesUpdateEvent.createPromise();
    source.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );
  }

  try {
    const response = await fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sectionIds.join(',') }),
    });
    const payload = await response.json();
    if (payload.status) {
      throw new Error(payload.description || payload.message || 'Add to cart failed');
    }

    const cart = await fetchCart();
    if (deferred && CartLinesUpdateEvent) {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: sourceId,
          sourceId,
          itemCount,
          sections: payload.sections,
          didError: false,
        },
      });
    } else {
      window.location.href = routes().cart_url || '/cart';
    }
    return cart;
  } catch (error) {
    deferred?.reject(error);
    throw error;
  }
}

/**
 * Runs `callback` after any cart lines update (ours, the drawer's, quick view...).
 * @param {() => void} callback
 * @param {AbortSignal} signal
 */
async function onCartUpdate(callback, signal) {
  const events = await getEvents();
  if (signal.aborted) return;
  const name = events?.StandardEvents?.cartLinesUpdate ?? FALLBACK_LINES_UPDATE;
  document.addEventListener(
    name,
    (event) => {
      const promise = /** @type {any} */ (event).promise;
      Promise.resolve(promise).then(callback, () => {});
    },
    { signal }
  );
}

/**
 * <bb-pdp-gallery> — one markup, two behaviours. Desktop shows a single slide at a time
 * driven by the thumbnail rail; below 750px the slides sit in a scroll-snap track and the
 * dots / counter follow the scroll position.
 */
class BBPdpGallery extends HTMLElement {
  /** @type {HTMLElement[]} */ #slides = [];
  /** @type {HTMLElement[]} */ #thumbs = [];
  /** @type {HTMLElement[]} */ #dots = [];
  /** @type {HTMLElement | null} */ #track = null;
  /** @type {HTMLElement | null} */ #counter = null;
  /** @type {MediaQueryList | null} */ #mobile = null;
  /** @type {AbortController | null} */ #abort = null;
  /** @type {HTMLDialogElement | null} */ #zoom = null;
  #index = 0;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    this.#thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.#dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));
    this.#track = this.querySelector('[data-gallery-track]');
    this.#counter = this.querySelector('[data-gallery-counter]');
    this.#mobile = window.matchMedia(MOBILE_QUERY);

    const active = this.#slides.findIndex((slide) => slide.hasAttribute('data-active'));
    this.#index = Math.max(0, active);

    this.#thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.index)), { signal });
    });

    this.#track?.addEventListener('scroll', () => this.#onScroll(), { passive: true, signal });
    this.#mobile.addEventListener('change', () => this.#syncPosition(false), { signal });
    this.addEventListener('keydown', (event) => this.#onKeydown(event), { signal });
    this.querySelector('[data-gallery-zoom]')?.addEventListener('click', () => this.#openZoom(), { signal });

    this.#syncPosition(false);
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#zoom?.remove();
    this.#zoom = null;
  }

  /** @param {number} index */
  goTo(index) {
    if (!Number.isInteger(index) || index < 0 || index >= this.#slides.length) return;
    this.#setActive(index);
    this.#syncPosition(true);
  }

  /** Jumps to the slide of a media id (variant featured media). @param {number | string | null} mediaId */
  goToMedia(mediaId) {
    if (mediaId == null) return;
    const index = this.#slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index !== -1) this.goTo(index);
  }

  /** Marks one slide as current in the DOM (slides, thumbs, dots, counter). @param {number} index */
  #setActive(index) {
    this.#index = index;
    this.#slides.forEach((slide, i) => slide.toggleAttribute('data-active', i === index));
    this.#thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
    this.#dots.forEach((dot, i) => dot.toggleAttribute('data-active', i === index));
    if (this.#counter) this.#counter.textContent = `${index + 1} / ${this.#slides.length}`;

    // Keep the active thumbnail visible inside the rail without scrolling the page itself.
    const thumb = this.#thumbs[index];
    const rail = thumb?.parentElement;
    if (thumb && rail && !this.#mobile?.matches && rail.scrollHeight > rail.clientHeight) {
      const top = thumb.offsetTop - (rail.clientHeight - thumb.offsetHeight) / 2;
      rail.scrollTo({ top, behavior: REDUCE_MOTION.matches ? 'auto' : 'smooth' });
    }
  }

  /** On mobile the track has to scroll to the active slide; desktop is purely CSS-driven. @param {boolean} animate */
  #syncPosition(animate) {
    if (!this.#mobile?.matches || !this.#track) return;
    const slide = this.#slides[this.#index];
    if (!slide) return;
    const behavior = animate && !REDUCE_MOTION.matches ? 'smooth' : 'auto';
    this.#track.scrollTo({ left: this.#index * this.#track.clientWidth, behavior });
  }

  #onScroll() {
    if (!this.#mobile?.matches || !this.#track || this.#slides.length < 2) return;
    const width = this.#track.clientWidth;
    if (!width) return;
    const index = Math.min(this.#slides.length - 1, Math.max(0, Math.round(this.#track.scrollLeft / width)));
    if (index !== this.#index) this.#setActive(index);
  }

  /** @param {KeyboardEvent} event */
  #onKeydown(event) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    if (!(event.target instanceof Element) || !event.target.closest('[data-gallery-track], [data-gallery-thumb]')) return;
    const next = this.#index + (event.key === 'ArrowRight' ? 1 : -1);
    if (next < 0 || next >= this.#slides.length) return;
    event.preventDefault();
    this.goTo(next);
    this.#thumbs[next]?.focus({ preventScroll: true });
  }

  #openZoom() {
    const src = this.#slides[this.#index]?.dataset.zoomSrc;
    if (!src) return;

    if (!this.#zoom) {
      const dialog = document.createElement('dialog');
      dialog.className = 'bb-pdp-zoom';
      dialog.setAttribute('aria-label', 'Imagen ampliada');

      const image = document.createElement('img');
      image.className = 'bb-pdp-zoom__img';
      image.alt = '';

      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'bb-pdp-zoom__close';
      close.setAttribute('aria-label', 'Cerrar');
      close.textContent = '×';
      close.addEventListener('click', () => dialog.close());

      dialog.append(image, close);
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
      this.append(dialog);
      this.#zoom = dialog;
    }

    const image = this.#zoom.querySelector('img');
    if (image) image.src = src;
    this.#zoom.showModal();
  }
}

/**
 * <bb-pdp-buy> — owns the purchase state (options, pack, quantity) and keeps every price
 * related element in sync. Data comes from the embedded [data-bb-pdp-data] JSON; the
 * server-rendered markup is the no-JS baseline.
 */
class BBPdpBuy extends HTMLElement {
  /** @type {any} */ #data = {};
  /** @type {any[]} */ #variants = [];
  /** @type {string[]} */ #selected = [];
  /** @type {any | null} */ #variant = null;
  /** @type {HTMLFormElement | null} */ #form = null;
  /** @type {AbortController | null} */ #abort = null;
  /** @type {Intl.NumberFormat | null} */ #money = null;
  #packIndex = 0;
  #quantity = 1;
  #maxQuantity = 10;
  #cartTotal = 0;
  #busy = false;
  /** @type {any | null} */ #state = null;

  /** Last published state, for the sticky bar that may connect after the first render. */
  get state() {
    return this.#state;
  }

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const script = this.querySelector('[data-bb-pdp-data]');
    try {
      this.#data = script ? JSON.parse(script.textContent || '{}') : {};
    } catch (error) {
      console.error('[bb-pdp] invalid product data', error);
      return;
    }

    this.#variants = this.#data.variants || [];
    this.#form = this.querySelector('form');
    this.#money = moneyFormatter(this.#data.currency);
    this.#cartTotal = Number(this.#data.cartTotal) || 0;
    this.#maxQuantity = Number(this.dataset.maxQuantity) || 10;

    this.#variant = this.#variants.find((v) => v.id === this.#data.selectedId) ?? this.#variants[0] ?? null;
    this.#selected = this.#variant ? [...this.#variant.options] : [];

    const checkedPack = this.querySelector('[data-bb-pdp-pack-input]:checked');
    this.#packIndex = checkedPack instanceof HTMLInputElement ? Number(checkedPack.dataset.packIndex) || 0 : 0;

    this.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        if (target.matches('[data-bb-pdp-option-input]')) {
          this.#selected[Number(target.dataset.optionIndex)] = target.value;
          this.#onVariantChosen();
        } else if (target.matches('[data-bb-pdp-pack-input]')) {
          this.#packIndex = Number(target.dataset.packIndex) || 0;
          this.#render();
        } else if (target.matches('[data-bb-pdp-qty]')) {
          this.#setQuantity(Number(target.value));
        }
      },
      { signal }
    );

    this.querySelector('[data-bb-pdp-dec]')?.addEventListener('click', () => this.#setQuantity(this.#quantity - 1), { signal });
    this.querySelector('[data-bb-pdp-inc]')?.addEventListener('click', () => this.#setQuantity(this.#quantity + 1), { signal });

    this.#form?.addEventListener(
      'submit',
      (event) => {
        event.preventDefault();
        this.add();
      },
      { signal }
    );

    this.#render();

    this.#refreshCart();
    onCartUpdate(() => this.#refreshCart(), signal);
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /**
   * Adds the current selection to the cart. Falls back to a native form submit when the
   * events module is unavailable.
   * @param {Element} [source] element to dispatch the cart event from (defaults to the main button)
   */
  async add(source) {
    if (this.#busy || !this.#variant?.available || !this.#form) return;

    const events = await getEvents();
    if (!events?.CartLinesUpdateEvent) {
      this.#form.submit();
      return;
    }

    const origin = source ?? this.querySelector('[data-bb-pdp-atc]') ?? this;
    this.#busy = true;
    this.#setError('');
    this.#render();

    try {
      const units = this.#pack().units;
      const cart = await addLines([{ id: this.#variant.id, quantity: units * this.#quantity }], origin, 'bb-pdp');
      this.#cartTotal = Number(cart.total_price) || this.#cartTotal;
    } catch (error) {
      console.error('[bb-pdp] add to cart failed', error);
      this.#setError(error instanceof Error && error.message ? error.message : this.#data.labels?.error || '');
    } finally {
      this.#busy = false;
      this.#render();
    }
  }

  async #refreshCart() {
    try {
      const cart = await fetchCart();
      this.#cartTotal = Number(cart.total_price) || 0;
      this.#render();
    } catch {
      // Keep the server-rendered cart total.
    }
  }

  /** @param {string} message */
  #setError(message) {
    const box = this.querySelector('[data-bb-pdp-error]');
    if (!box) return;
    box.textContent = message;
    box.toggleAttribute('hidden', !message);
  }

  #pack() {
    const packs = this.#data.packs;
    const fallback = { name: this.#data.labels?.oneUnit || '1 unidad', units: 1, discount: 0 };
    if (!packs?.enabled) return fallback;
    return packs.items?.[this.#packIndex] ?? fallback;
  }

  /** @param {number} value */
  #setQuantity(value) {
    const next = Number.isFinite(value) ? Math.round(value) : 1;
    this.#quantity = Math.min(this.#maxQuantity, Math.max(1, next));
    this.#render();
  }

  /** Variant picked by the user: sync URL and jump the gallery to the variant image. */
  #onVariantChosen() {
    const variant = this.#resolveVariant();
    this.#render();
    if (!variant) return;

    if (this.#data.hasVariants) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(variant.id));
        window.history.replaceState(window.history.state, '', url);
      } catch {
        // history unavailable (sandboxed preview): the URL is a nicety only.
      }
    }
    const root = this.closest('[data-bb-pdp-root]');
    const gallery = root?.querySelector('bb-pdp-gallery');
    if (gallery && typeof gallery.goToMedia === 'function') gallery.goToMedia(variant.mediaId);
  }

  #resolveVariant() {
    const match = this.#variants.find((v) => v.options.every((value, i) => value === this.#selected[i]));
    this.#variant = match ?? null;
    return match ?? null;
  }

  /** @param {number} cents */
  #fmt(cents) {
    return this.#money.format(cents / 100);
  }

  /** Recomputes every derived value and pushes it into the DOM. */
  #render() {
    const labels = this.#data.labels || {};
    const variant = this.#resolveVariant();
    const pack = this.#pack();
    const exists = Boolean(variant);
    const available = exists && variant.available;

    // Fall back to the last known variant data for prices when the combination does not exist.
    const priced = variant ?? this.#variants.find((v) => v.id === this.#data.selectedId) ?? this.#variants[0];
    const unitPrice = priced?.price ?? 0;
    const unitCompare = priced && priced.compareAtPrice > priced.price ? priced.compareAtPrice : priced?.price ?? 0;
    const keep = (100 - pack.discount) / 100;
    const units = pack.units * this.#quantity;
    const total = Math.round(unitPrice * units * keep);
    const compare = Math.round(unitCompare * units);
    const points = Math.floor((total / 100) * (Number(this.#data.pointsPerEuro) || 0));

    this.#text('[data-bb-pdp-price]', this.#fmt(total));
    const compareEl = this.querySelector('[data-bb-pdp-compare]');
    if (compareEl) {
      compareEl.textContent = this.#fmt(compare);
      compareEl.toggleAttribute('hidden', !(compare > total));
    }
    const pointsEl = this.querySelector('[data-bb-pdp-points]');
    if (pointsEl) {
      pointsEl.toggleAttribute('hidden', points < 1);
      this.#text('[data-bb-pdp-points-value]', String(points));
    }

    this.#renderOptions();
    this.#renderPacks(unitPrice, unitCompare);

    const idInput = this.querySelector('[data-bb-pdp-id]');
    if (idInput instanceof HTMLInputElement) {
      const changed = exists && idInput.value !== String(variant.id);
      if (exists) idInput.value = String(variant.id);
      idInput.disabled = !available;
      // Shopify's dynamic checkout button watches this input to follow the variant.
      if (changed) idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    const quantityInput = this.querySelector('[data-bb-pdp-quantity]');
    if (quantityInput instanceof HTMLInputElement) quantityInput.value = String(units);
    const qtyInput = this.querySelector('[data-bb-pdp-qty]');
    if (qtyInput instanceof HTMLInputElement) qtyInput.value = String(this.#quantity);
    this.#toggle('[data-bb-pdp-dec]', 'disabled', this.#quantity <= 1);
    this.#toggle('[data-bb-pdp-inc]', 'disabled', this.#quantity >= this.#maxQuantity);

    const atc = this.querySelector('[data-bb-pdp-atc]');
    if (atc instanceof HTMLButtonElement) atc.disabled = !available || this.#busy;
    this.#text('[data-bb-pdp-atc-label]', this.#busy ? labels.adding || '' : available ? labels.add : labels.soldOut);
    this.#toggle('[data-bb-pdp-atc-dot]', 'hidden', !available || this.#busy);
    this.#toggle('[data-bb-pdp-atc-total]', 'hidden', !available || this.#busy);
    this.#text('[data-bb-pdp-atc-total]', this.#fmt(total));
    this.#toggle('[data-bb-pdp-buynow]', 'hidden', !available);

    this.#renderShipping(total);

    const label = this.#summaryLabel(pack, priced);
    const thumb = priced?.thumb || this.#data.defaultThumb || '';
    this.#state = { label, total: this.#fmt(total), thumb, available, addLabel: available ? labels.add : labels.soldOut, busy: this.#busy };
    this.dispatchEvent(new CustomEvent('bb-pdp:state', { bubbles: true, detail: this.#state }));
  }

  /** "Pack 2 · 500 ml ×2" style label for the sticky bar. */
  #summaryLabel(pack, variant) {
    const labels = this.#data.labels || {};
    const parts = [];
    if (this.#data.packs?.enabled) parts.push(pack.name);
    else parts.push(this.#quantity === 1 ? labels.oneUnit : (labels.manyUnits || '[n]').replace('[n]', String(this.#quantity)));
    if (this.#data.hasVariants && variant?.title) parts.push(variant.title);
    if (this.#data.packs?.enabled && this.#quantity > 1) parts.push(`×${this.#quantity}`);
    return parts.join(' · ');
  }

  /** Selected labels and "unavailable" marks, evaluated against the other chosen options. */
  #renderOptions() {
    this.querySelectorAll('[data-bb-pdp-option]').forEach((fieldset) => {
      const index = Number(fieldset.dataset.optionIndex);
      const selectedLabel = fieldset.querySelector('[data-selected-label]');
      if (selectedLabel) selectedLabel.textContent = this.#selected[index] ?? '';

      fieldset.querySelectorAll('[data-bb-pdp-option-input]').forEach((input) => {
        if (!(input instanceof HTMLInputElement)) return;
        input.checked = input.value === this.#selected[index];
        const inStock = this.#variants.some(
          (v) =>
            v.available &&
            v.options[index] === input.value &&
            v.options.every((value, i) => i === index || value === this.#selected[i])
        );
        const pill = input.closest('.bb-pdp__pill');
        if (inStock) pill?.removeAttribute('data-unavailable');
        else pill?.setAttribute('data-unavailable', 'true');
      });
    });
  }

  /** @param {number} unitPrice cents @param {number} unitCompare cents */
  #renderPacks(unitPrice, unitCompare) {
    if (!this.#data.packs?.enabled) return;
    const items = this.#data.packs.items || [];
    const saveTemplate = this.#data.labels?.save || 'Ahorras [%] %';

    this.querySelectorAll('[data-bb-pdp-pack-card]').forEach((card) => {
      const index = Number(card.dataset.packIndex);
      const pack = items[index];
      if (!pack) return;
      const total = Math.round(unitPrice * pack.units * ((100 - pack.discount) / 100));
      const raw = Math.round(unitCompare * pack.units);

      const input = card.querySelector('[data-bb-pdp-pack-input]');
      if (input instanceof HTMLInputElement) input.checked = index === this.#packIndex;
      this.#cardText(card, '[data-pack-total]', this.#fmt(total));
      this.#cardText(card, '[data-pack-unit]', `${this.#fmt(Math.round(total / pack.units))} / ud`);

      const save = card.querySelector('[data-pack-save]');
      if (save) {
        save.textContent = saveTemplate.replace('[%]', String(pack.discount));
        save.toggleAttribute('hidden', !(pack.discount > 0));
      }
      const compare = card.querySelector('[data-pack-compare]');
      if (compare) {
        compare.textContent = this.#fmt(raw);
        compare.toggleAttribute('hidden', !(raw > total));
      }
    });
  }

  /** @param {number} selectionTotal cents */
  #renderShipping(selectionTotal) {
    const threshold = Number(this.#data.threshold) || 0;
    if (threshold <= 0) return;
    const basis = this.#cartTotal + selectionTotal;
    const remaining = threshold - basis;
    const text = this.querySelector('[data-bb-pdp-ship-text]');
    if (text) {
      if (remaining > 0) setTemplate(text, this.#data.labels?.shipRemaining || '', 'importe', this.#fmt(remaining));
      else text.textContent = this.#data.labels?.shipFree || '';
    }
    const fill = this.querySelector('[data-bb-pdp-ship-fill]');
    if (fill instanceof HTMLElement) fill.style.width = `${Math.min(100, Math.max(0, Math.round((basis / threshold) * 100)))}%`;
  }

  /** @param {string} selector @param {string} value */
  #text(selector, value) {
    const el = this.querySelector(selector);
    if (el && el.textContent !== value) el.textContent = value;
  }

  /** @param {Element} card @param {string} selector @param {string} value */
  #cardText(card, selector, value) {
    const el = card.querySelector(selector);
    if (el) el.textContent = value;
  }

  /** @param {string} selector @param {'disabled' | 'hidden'} attribute @param {boolean} on */
  #toggle(selector, attribute, on) {
    this.querySelector(selector)?.toggleAttribute(attribute, on);
  }
}

/**
 * <bb-pdp-sticky> — mobile add-to-cart bar. Visible only while the main add button is out of
 * view and the footer is not on screen; mirrors the buy box state and triggers its add flow.
 */
class BBPdpSticky extends HTMLElement {
  /** @type {AbortController | null} */ #abort = null;
  /** @type {IntersectionObserver | null} */ #observer = null;
  #atcVisible = true;
  #footerVisible = false;
  /** @type {HTMLButtonElement | null} */ #button = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const root = this.closest('[data-bb-pdp-root]');
    const buy = root?.querySelector('bb-pdp-buy');
    this.#button = this.querySelector('[data-sticky-add]');
    if (!root || !buy) return;

    root.addEventListener('bb-pdp:state', (event) => this.#apply(/** @type {CustomEvent} */ (event).detail), { signal });
    if (buy.state) this.#apply(buy.state);

    this.#button?.addEventListener('click', () => buy.add(this.#button), { signal });

    const atc = buy.querySelector('[data-bb-pdp-atc]');
    const footer = document.querySelector('#footer-group, footer');
    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === atc) this.#atcVisible = entry.isIntersecting;
          else this.#footerVisible = entry.isIntersecting;
        });
        this.#update();
      });
      if (atc) this.#observer.observe(atc);
      if (footer) this.#observer.observe(footer);
    }
    this.#update();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
  }

  #update() {
    this.toggleAttribute('data-visible', !this.#atcVisible && !this.#footerVisible);
  }

  /** @param {{ label: string, total: string, thumb: string, available: boolean, addLabel: string, busy: boolean }} state */
  #apply(state) {
    const label = this.querySelector('[data-sticky-label]');
    const price = this.querySelector('[data-sticky-price]');
    const thumb = this.querySelector('[data-sticky-thumb]');
    if (label) label.textContent = state.label;
    if (price) price.textContent = state.total;
    if (thumb instanceof HTMLImageElement && state.thumb && thumb.getAttribute('src') !== state.thumb) thumb.src = state.thumb;
    if (this.#button) {
      this.#button.disabled = !state.available || state.busy;
      this.#button.textContent = state.addLabel;
    }
  }
}

/**
 * <bb-pdp-fbt> — "Suelen comprarse juntos". The current product card is server-rendered; when
 * no products are picked in the section settings the companions are fetched from Shopify's
 * complementary recommendations (data-fetch-url) and swapped into the slot. The whole block
 * stays hidden until there is at least one companion.
 */
class BBPdpFbt extends HTMLElement {
  /** @type {AbortController | null} */ #abort = null;
  /** @type {Intl.NumberFormat | null} */ #money = null;
  #cartTotal = 0;
  #busy = false;

  connectedCallback() {
    this.#abort = new AbortController();
    this.#money = moneyFormatter(this.dataset.currency || 'EUR');
    const url = this.dataset.fetchUrl;
    if (url) this.#load(url);
    else this.#init();
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /** @param {string} url */
  async #load(url) {
    try {
      const response = await fetch(url, { signal: this.#abort?.signal });
      if (!response.ok) return;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const payload = doc.querySelector('[data-bb-fbt-others]');
      const slot = this.querySelector('[data-bb-fbt-slot]');
      if (!payload || !slot || !payload.querySelector('[data-bb-fbt-item]')) return;
      slot.innerHTML = payload.innerHTML;
      this.style.setProperty('--bb-fbt-count', String(this.querySelectorAll('[data-bb-fbt-item]').length));
      this.removeAttribute('data-fetch-url');
      this.hidden = false;
      this.#init();
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[bb-pdp-fbt] recommendations failed', error);
    }
  }

  #init() {
    const signal = this.#abort?.signal;
    this.querySelectorAll('[data-bb-fbt-toggle]').forEach((button) => {
      button.addEventListener(
        'click',
        () => {
          const on = button.getAttribute('aria-pressed') !== 'true';
          button.setAttribute('aria-pressed', String(on));
          const item = button.closest('[data-bb-fbt-item]');
          item?.toggleAttribute('data-off', !on);
          button.setAttribute('aria-label', `${on ? this.dataset.removeLabel : this.dataset.addLabel} ${item?.dataset.name ?? ''}`.trim());
          this.#render();
        },
        { signal }
      );
    });
    this.querySelector('[data-bb-fbt-add]')?.addEventListener('click', (event) => this.#add(event.currentTarget), { signal });

    this.#render();
    this.#refreshCart();
    if (signal) onCartUpdate(() => this.#refreshCart(), signal);
  }

  async #refreshCart() {
    try {
      this.#cartTotal = Number((await fetchCart()).total_price) || 0;
      this.#render();
    } catch {
      // Keep going without the cart total.
    }
  }

  #selected() {
    return Array.from(this.querySelectorAll('[data-bb-fbt-item]')).filter((item) => !item.hasAttribute('data-off'));
  }

  #render() {
    const selected = this.#selected();
    const total = selected.reduce((sum, item) => sum + Number(item.dataset.price || 0), 0);
    const count = selected.length;

    const unit = count === 1 ? this.dataset.unitOne : this.dataset.unitMany;
    this.#set('[data-bb-fbt-count]', `${this.dataset.summaryLabel} · ${count} ${unit}`);
    this.#set('[data-bb-fbt-total]', this.#money.format(total / 100));

    const threshold = Number(this.dataset.threshold) || 0;
    const message = this.querySelector('[data-bb-fbt-ship]');
    if (message) {
      if (threshold <= 0) {
        message.hidden = true;
      } else {
        message.hidden = false;
        const remaining = threshold - (this.#cartTotal + total);
        if (remaining > 0) message.textContent = (this.dataset.shipLeft || '').replace('[importe]', this.#money.format(remaining / 100));
        else message.textContent = this.dataset.shipFree || '';
      }
    }

    const button = this.querySelector('[data-bb-fbt-add]');
    if (button instanceof HTMLButtonElement) {
      button.disabled = count === 0 || this.#busy;
      button.textContent = this.#busy ? this.dataset.addingLabel || '' : (this.dataset.addTemplate || '').replace('[n]', String(count));
    }
  }

  /** @param {Element | null} source */
  async #add(source) {
    if (this.#busy || !source) return;
    const items = this.#selected().map((item) => ({ id: Number(item.dataset.variantId), quantity: 1 }));
    if (items.length === 0) return;

    this.#busy = true;
    const error = this.querySelector('[data-bb-fbt-error]');
    if (error) error.hidden = true;
    this.#render();
    try {
      const cart = await addLines(items, source, 'bb-pdp-fbt');
      this.#cartTotal = Number(cart.total_price) || this.#cartTotal;
    } catch (failure) {
      console.error('[bb-pdp-fbt] add to cart failed', failure);
      if (error) {
        error.textContent = failure instanceof Error && failure.message ? failure.message : this.dataset.errorLabel || '';
        error.hidden = false;
      }
    } finally {
      this.#busy = false;
      this.#render();
    }
  }

  /** @param {string} selector @param {string} value */
  #set(selector, value) {
    const el = this.querySelector(selector);
    if (el) el.textContent = value;
  }
}

if (!customElements.get('bb-pdp-gallery')) customElements.define('bb-pdp-gallery', BBPdpGallery);
if (!customElements.get('bb-pdp-buy')) customElements.define('bb-pdp-buy', BBPdpBuy);
if (!customElements.get('bb-pdp-sticky')) customElements.define('bb-pdp-sticky', BBPdpSticky);
if (!customElements.get('bb-pdp-fbt')) customElements.define('bb-pdp-fbt', BBPdpFbt);
